import { useMemo } from 'react';
import type { ContactApprovalState, Email } from '@/types/email';
import type { ActiveSenderGroup, SenderGroupInfo } from '../types';
import { resolveContactApprovalState } from '../utils/emailFormatting';
import { useSenderGroupingStore } from '@/stores/senderGroupingStore';

export interface SenderGroupEntry {
  key: string;
  contactId?: string | null;
  senderEmail: string;
  displayEmail: string;
  senderName?: string | null;
  emails: Email[];
  unreadCount: number;
  lastMessageDate: Date;
  contactApprovalState?: ContactApprovalState | null;
  totalMessages: number | null;
}

interface SenderGroupingResult {
  groups: SenderGroupEntry[];
  map: Map<string, SenderGroupEntry>;
  allEmails: Email[];
}

export type GroupingStrategy = 'sender' | 'recipient';

interface SenderGroupingOptions {
  activeGroupKey?: string | null;
  activeGroup?: ActiveSenderGroup | null | undefined;
  accountEmails?: Set<string>;
}

interface GroupingTarget {
  key: string;
  contactId?: string | null;
  email: string;
  displayEmail: string;
  name?: string | null;
}

const normalizeEmail = (value?: string | null) => value?.trim().toLowerCase() ?? '';

const getPrimaryRecipient = (email: Email, accountEmails?: Set<string>) => {
  const candidateLists = [email.to, email.cc, email.bcc, email.replyTo];
  for (const list of candidateLists) {
    if (!list) {
      continue;
    }
    // If accountEmails is provided, find first non-account recipient
    const recipient = accountEmails
      ? list.find((entry) => entry?.email && !accountEmails.has(normalizeEmail(entry.email)))
      : list.find((entry) => entry && entry.email);
    if (recipient && recipient.email) {
      return recipient;
    }
  }
  return null;
};

interface ResolveGroupingTargetOptions {
  strategy: GroupingStrategy;
  accountEmails?: Set<string>;
}

/**
 * Check if an email is sent by the user (from one of their accounts)
 */
const isEmailFromUser = (email: Email, accountEmails?: Set<string>): boolean => {
  if (!accountEmails) return false;
  const senderEmail = normalizeEmail(email.from.email);
  return Boolean(senderEmail && accountEmails.has(senderEmail));
};

const resolveGroupingTarget = (email: Email, options: ResolveGroupingTargetOptions): GroupingTarget => {
  const { strategy, accountEmails } = options;

  // For 'sender' strategy, if the email is FROM one of the user's accounts,
  // use the primary recipient instead (so threads stay grouped with the other party)
  const isFromUser = isEmailFromUser(email, accountEmails);

  if (strategy === 'recipient' || (strategy === 'sender' && isFromUser)) {
    const recipient = getPrimaryRecipient(email, accountEmails);
    if (recipient?.email) {
      const normalizedEmail = normalizeEmail(recipient.email);
      const fallbackKey = `recipient-${email.id}`;
      const displayEmail = recipient.email || email.from.email || fallbackKey;
      return {
        key: normalizedEmail || fallbackKey,
        contactId: null,
        email: recipient.email || displayEmail,
        displayEmail,
        name: recipient.name ?? recipient.email ?? null,
      };
    }
  }

  const normalizedSenderEmail = normalizeEmail(email.from.email);
  const fallbackKey = `sender-${email.id}`;
  const displayEmail = email.from.email || normalizedSenderEmail || fallbackKey;
  const key = normalizedSenderEmail || fallbackKey;
  return {
    key,
    contactId: email.contactId ?? null,
    email: email.from.email || displayEmail,
    displayEmail,
    name: email.from.name ?? email.from.email ?? null,
  };
};

interface UseSenderGroupingArgs {
  emails: Email[];
  groupBySender: boolean;
  activeGroupKey: string | null;
  activeSenderGroup: ActiveSenderGroup | null | undefined;
  groupingStrategy: GroupingStrategy;
  accountEmails?: Set<string>;
}
const buildSenderGrouping = (
  source: Email[],
  strategy: GroupingStrategy,
  options: SenderGroupingOptions = {},
): SenderGroupingResult => {
  if (!source.length) {
    return { groups: [], map: new Map(), allEmails: [] };
  }

  const map = new Map<string, SenderGroupEntry & { emailsMap: Map<string, Email> }>();

  source.forEach((email) => {
    const target = resolveGroupingTarget(email, { strategy, accountEmails: options.accountEmails });
    const groupKey = target.key;
    const existing = map.get(groupKey);
    const emailDate = email.date instanceof Date ? email.date : new Date(email.date);
    const entry =
      existing ??
      {
        key: groupKey,
        contactId: target.contactId ?? null,
        senderEmail: target.email || target.displayEmail,
        displayEmail: target.displayEmail || target.email,
        senderName: target.name ?? target.displayEmail ?? null,
        emails: [],
        emailsMap: new Map<string, Email>(),
        unreadCount: 0,
        lastMessageDate: emailDate,
        contactApprovalState: email.contactApprovalState ?? null,
        totalMessages: null,
      };

    if (!entry.emailsMap.has(email.id)) {
      entry.emailsMap.set(email.id, email);
      if (!email.read) {
        entry.unreadCount += 1;
      }
      if (emailDate > entry.lastMessageDate) {
        entry.lastMessageDate = emailDate;
        entry.senderEmail = target.email || entry.senderEmail;
        entry.displayEmail = target.displayEmail || entry.displayEmail;
        entry.senderName = target.name ?? entry.senderName;
        if (target.contactId && entry.contactId !== target.contactId) {
          entry.contactId = target.contactId;
        }
      }
    } else {
      const existingEmail = entry.emailsMap.get(email.id);
      if (existingEmail) {
        if (existingEmail.read && !email.read) {
          entry.unreadCount += 1;
        } else if (!existingEmail.read && email.read) {
          entry.unreadCount = Math.max(0, entry.unreadCount - 1);
        }
      }
      entry.emailsMap.set(email.id, email);
      if (target.contactId && entry.contactId !== target.contactId) {
        entry.contactId = target.contactId;
      }
    }

    if (entry.contactApprovalState !== 'blocked') {
      entry.contactApprovalState = resolveContactApprovalState(
        entry.contactApprovalState,
        email.contactApprovalState,
      );
    }

    // Use senderMailboxCount from emails - server already computes cross-account totals
    // for unified view, so we just take the max value from any email
    if (
      strategy === 'sender' &&
      typeof email.senderMailboxCount === 'number' &&
      Number.isFinite(email.senderMailboxCount)
    ) {
      const currentTotal = entry.totalMessages ?? 0;
      if (email.senderMailboxCount > currentTotal) {
        entry.totalMessages = email.senderMailboxCount;
      }
    }

    if (!existing) {
      map.set(groupKey, entry);
    }
  });

  const aggregatedEmails: Email[] = [];
  const groups: SenderGroupEntry[] = Array.from(map.values()).map((group) => {
    // Pre-compute timestamps to avoid creating Date objects in the sort comparator
    const emailsWithTs = Array.from(group.emailsMap.values()).map((email) => ({
      email,
      ts: email.date instanceof Date ? email.date.getTime() : new Date(email.date).getTime(),
    }));
    emailsWithTs.sort((a, b) => {
      const timeDiff = b.ts - a.ts;
      if (timeDiff !== 0) return timeDiff;
      if (a.email.id < b.email.id) return -1;
      if (a.email.id > b.email.id) return 1;
      return 0;
    });
    const emailsArray = emailsWithTs.map((item) => item.email);

    if (emailsArray.length) {
      aggregatedEmails.push(...emailsArray);
    }
    const totalMessages =
      strategy === 'sender'
        ? typeof group.totalMessages === 'number' && Number.isFinite(group.totalMessages)
          ? Math.max(group.totalMessages, emailsArray.length)
          : null
        : emailsArray.length;
    return {
      key: group.key,
      contactId: group.contactId,
      senderEmail: group.senderEmail,
      displayEmail: group.displayEmail,
      senderName: group.senderName,
      emails: emailsArray,
      unreadCount: group.unreadCount,
      lastMessageDate: emailsArray[0]?.date
        ? new Date(emailsArray[0].date)
        : group.lastMessageDate,
      contactApprovalState: group.contactApprovalState ?? null,
      totalMessages,
    };
  });

  // Filter out groups with no visible emails (e.g., groups that only had sent emails)
  let visibleGroups = groups.filter((group) => group.emails.length > 0);

  // Debug logging for filtered out groups
  if (import.meta.env.DEV) {
    const emptyGroups = groups.filter((group) => group.emails.length === 0);
    if (emptyGroups.length > 0) {
      console.log('[useSenderGrouping] Groups filtered out (no visible emails):', {
        filteredCount: emptyGroups.length,
        filteredKeys: emptyGroups.map(g => g.key),
        visibleCount: visibleGroups.length,
      });
    }
  }

  // Sort visible groups by last message date with key as stable tie-breaker
  visibleGroups.sort((a, b) => {
    const timeDiff = b.lastMessageDate.getTime() - a.lastMessageDate.getTime();
    if (timeDiff !== 0) return timeDiff;
    // Stable secondary sort by group key
    if (a.key < b.key) return -1;
    if (a.key > b.key) return 1;
    return 0;
  });

  if (options.activeGroupKey) {
    const normalizedActiveEmail = normalizeEmail(options.activeGroup?.email);
    const activeContactId = options.activeGroup?.contactId ?? null;
    // Rebind WITHOUT mutating the (memoized) group objects, and rebind only
    // the FIRST match — the predicate can match several groups (same contact
    // across accounts), and giving two groups the same key produced duplicate
    // React keys in the virtualizer (C16).
    let rebound = false;
    visibleGroups = visibleGroups.map((group) => {
      if (rebound) {
        return group;
      }
      const normalizedGroupEmail = normalizeEmail(group.senderEmail);
      const isActive =
        group.key === options.activeGroupKey ||
        (activeContactId && group.contactId && group.contactId === activeContactId) ||
        (normalizedActiveEmail && normalizedGroupEmail === normalizedActiveEmail);
      if (!isActive) {
        return group;
      }
      rebound = true;
      return {
        ...group,
        key: options.activeGroupKey ?? group.key,
        contactId: group.contactId ?? activeContactId ?? null,
      };
    });
  }

  const normalizedMap = new Map<string, SenderGroupEntry>();
  visibleGroups.forEach((group) => {
    normalizedMap.set(group.key, group);
    if (group.contactId) {
      normalizedMap.set(`contact:${group.contactId}`, group);
    }
    const normalizedSender = normalizeEmail(group.senderEmail);
    if (normalizedSender) {
      normalizedMap.set(`email:${normalizedSender}`, group);
    }
  });

  return {
    groups: visibleGroups,
    map: normalizedMap,
    allEmails: aggregatedEmails,
  };
};

export function useSenderGrouping({
  emails,
  groupBySender,
  activeGroupKey,
  activeSenderGroup,
  groupingStrategy,
  accountEmails,
}: UseSenderGroupingArgs) {
  // Get cached accurate totals from store (populated by prefetch/select)
  const senderTotalsCache = useSenderGroupingStore((state) => state.senderTotalsCache);

  // The unread list filter is gone (round 4); the list always shows all mail.
  const filteredEmails = emails;

  // Unified grouping that avoids double computation
  const senderGroupingResult = useMemo(() => {
    if (!groupBySender) {
      return {
        groups: [],
        map: new Map<string, SenderGroupEntry>(),
        allEmails: [],
        activeGroup: null as SenderGroupEntry | null,
      };
    }

    const mainGrouping = buildSenderGrouping(filteredEmails, groupingStrategy, {
      activeGroupKey,
      activeGroup: activeSenderGroup,
      accountEmails,
    });

    return {
      ...mainGrouping,
      activeGroup: null as SenderGroupEntry | null,
    };
  }, [
    filteredEmails,
    groupBySender,
    groupingStrategy,
    activeGroupKey,
    activeSenderGroup,
    accountEmails,
  ]);

  // Apply cached accurate totals to groups (from prefetch/select API calls).
  // The cache is populated by targeted sender digest API calls which give more
  // accurate counts than the initial senderMailboxCount on page load emails.
  const senderGrouping = useMemo(() => {
    const groups = senderGroupingResult.groups.map((group) => {
      const cachedTotal = senderTotalsCache.get(group.key);
      if (cachedTotal !== undefined && cachedTotal !== (group.totalMessages ?? 0)) {
        return { ...group, totalMessages: cachedTotal };
      }
      return group;
    });

    // Rebuild map with updated groups
    const map = new Map<string, SenderGroupEntry>();
    groups.forEach((group) => {
      map.set(group.key, group);
      if (group.contactId) {
        map.set(`contact:${group.contactId}`, group);
      }
      const normalizedSender = group.senderEmail?.toLowerCase().trim();
      if (normalizedSender) {
        map.set(`email:${normalizedSender}`, group);
      }
    });

    return {
      groups,
      map,
      allEmails: senderGroupingResult.allEmails,
    };
  }, [senderGroupingResult, senderTotalsCache]);

  const activeSenderGrouping = useMemo(() => {
    if (!groupBySender || !activeGroupKey) {
      return senderGrouping;
    }
    // If we have an active group from the unified result, use it
    if (senderGroupingResult.activeGroup) {
      return {
        groups: senderGrouping.groups,
        map: senderGrouping.map,
        allEmails: senderGrouping.allEmails,
      };
    }
    // Fallback: build from all emails (shouldn't happen often)
    return buildSenderGrouping(emails, groupingStrategy, {
      activeGroupKey,
      activeGroup: activeSenderGroup,
      accountEmails,
    });
  }, [
    groupBySender,
    activeGroupKey,
    senderGrouping,
    senderGroupingResult.activeGroup,
    emails,
    groupingStrategy,
    activeSenderGroup,
    accountEmails,
  ]);

  const senderGroups = senderGrouping.groups;
  const senderGroupMap = senderGrouping.map;
  const groupedEmails = senderGrouping.allEmails;
  // Use activeGroup from unified result if available, otherwise fall back to map lookup
  let activeSenderData =
    activeGroupKey && senderGroupingResult.activeGroup
      ? senderGroupingResult.activeGroup
      : activeGroupKey
        ? activeSenderGrouping.map.get(activeGroupKey) ?? null
        : null;
  if (!activeSenderData && activeSenderGroup?.contactId) {
    activeSenderData =
      activeSenderGrouping.map.get(`contact:${activeSenderGroup.contactId}`) ?? activeSenderData;
  }
  if (!activeSenderData && activeSenderGroup?.email) {
    activeSenderData =
      activeSenderGrouping.map.get(`email:${normalizeEmail(activeSenderGroup.email)}`) ??
      activeSenderData;
  }

  const activeSenderInfo: SenderGroupInfo | null = useMemo(() => {
    if (!activeSenderData) {
      return null;
    }
    return {
      key: activeSenderData.key,
      contactId: activeSenderData.contactId ?? null,
      contactApprovalState: activeSenderData.contactApprovalState ?? null,
      senderEmail: activeSenderData.senderEmail,
      senderName:
        activeSenderData.senderName ??
        activeSenderGroup?.name ??
        activeSenderGroup?.email ??
        activeSenderData.displayEmail,
      emails: activeSenderData.emails,
      unreadCount: activeSenderData.unreadCount,
      totalMessages:
        typeof activeSenderData.totalMessages === 'number' && Number.isFinite(activeSenderData.totalMessages)
          ? Math.max(activeSenderData.totalMessages, activeSenderData.emails.length)
          : null,
    };
  }, [activeSenderData, activeSenderGroup]);

  const visibleEmails = useMemo(() => {
    if (groupBySender) {
      if (activeGroupKey) {
        const result = activeSenderData?.emails ?? [];
        // Debug logging to track email list changes
        if (import.meta.env.DEV) {
          console.log('[useSenderGrouping] visibleEmails:', {
            activeGroupKey,
            activeSenderDataExists: !!activeSenderData,
            emailCount: result.length,
            firstEmail: result[0]?.subject,
            lastEmail: result[result.length - 1]?.subject,
          });
        }
        return result;
      }
      // When showing grouped summary, return all emails from all groups for bulk selection
      return groupedEmails;
    }
    return filteredEmails;
  }, [filteredEmails, groupBySender, activeGroupKey, activeSenderData, groupedEmails]);

  const showGroupedSummary = groupBySender && !activeGroupKey;

  return {
    filteredEmails,
    visibleEmails,
    senderGroups,
    senderGroupMap,
    activeSenderData,
    activeSenderInfo,
    showGroupedSummary,
  };
}
